import { type ReactElement, useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { type CartePublique, DIMENSIONS_CARTE, ECHELLE_IMPRESSION } from "../contract";
import { Rang } from "../viewers/rang";
import { adresseDePartage, useCartePublique } from "./api";
import {
  AU_REPOS,
  type Inclinaison,
  inclinaisonDe,
  prefereMoinsDeMouvement,
  transformDe,
} from "./inclinaison";
import {
  ACCUEIL_PATH,
  ERREUR,
  editionEnToutesLettres,
  etatEdition,
  habitants,
  INCONNUE,
  nomRarete,
  PAGE,
  texteAlternatif,
  tirage,
  titreDeLOnglet,
} from "./publique-texts";
import { SignalementDialog } from "./signalement";

/**
 * La page publique d'une carte, `/c/<numero>-<edition>` (lot 5 du TCG, PR 5, maquette `carte.html` etats `recto` et
 * `signaler`) : l'image imprimee de la carte, inclinable au doigt ou a la souris, son nom, son rang, sa chronique en
 * texte (lisible par un lecteur d'ecran, avec la mention de l'IA), ses stats, combien d'habitants l'ont, et deux
 * gestes : partager (copie l'adresse d'apercu, celle que Discord sait lire) et signaler.
 *
 * Ouverte a tous, sans session. Aucun numero d'exemplaire : aucun exemplaire n'est designe ici (ecart 7 du plan).
 * Une carte inconnue, retiree ou pas encore validee : la meme page « Cette carte n'existe pas ou a ete retiree ».
 */

const LIEN =
  "text-primary no-underline hover:text-[#FCDD8E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const BOUTON =
  "min-h-11 rounded-md border-2 px-4 text-[20px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** La rarete : le blason des six rangs, un cartouche pour le Chatelain. Toujours son nom ecrit. */
function Rarete({ carte }: { carte: CartePublique }): ReactElement {
  if (carte.rarete === "chatelain") {
    return (
      <span
        data-rang="chatelain"
        className="police-texte inline-flex items-center rounded-[4px] border-2 border-[#F7BE6A] bg-background px-2.5 py-0.5 text-[20px] leading-tight text-card-foreground"
      >
        {nomRarete("chatelain")}
      </span>
    );
  }
  return <Rang id={carte.rarete} />;
}

/** L'image de la carte, qui s'incline vers le pointeur (sauf si le visiteur a demande moins de mouvement). */
function ImageInclinable({ carte }: { carte: CartePublique }): ReactElement {
  const boite = useRef<HTMLDivElement>(null);
  const [inclinaison, setInclinaison] = useState<Inclinaison>(AU_REPOS);
  const [mouvement] = useState(() => !prefereMoinsDeMouvement());
  const { largeur, hauteur } = DIMENSIONS_CARTE[carte.rarete];
  const incline = inclinaison !== AU_REPOS;

  function suivre(evenement: React.PointerEvent<HTMLDivElement>): void {
    const element = boite.current;
    if (!mouvement || element === null) return;
    const rect = element.getBoundingClientRect();
    setInclinaison(
      inclinaisonDe(
        { x: evenement.clientX, y: evenement.clientY },
        { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      ),
    );
  }

  return (
    <div
      ref={boite}
      data-testid="carte-inclinable"
      data-incline={incline ? "oui" : "non"}
      onPointerMove={suivre}
      onPointerLeave={() => setInclinaison(AU_REPOS)}
      onPointerCancel={() => setInclinaison(AU_REPOS)}
      style={{
        transform: mouvement ? transformDe(inclinaison) : undefined,
        touchAction: "pan-y",
      }}
      className={cn(
        "relative mx-auto w-full max-w-[420px] overflow-hidden rounded-[10px] shadow-[0_8px_0_var(--nuit)]",
        mouvement && "transition-transform duration-100 ease-out will-change-transform",
      )}
    >
      <img
        src={carte.image}
        alt={texteAlternatif({
          nom: carte.nom,
          rarete: carte.rarete,
          edition: carte.edition,
          chronique: carte.chronique,
        })}
        width={largeur * ECHELLE_IMPRESSION}
        height={hauteur * ECHELLE_IMPRESSION}
        draggable={false}
        className="block h-auto w-full select-none"
      />
      {mouvement && incline ? (
        <span
          aria-hidden="true"
          data-testid="reflet"
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(circle at ${inclinaison.reflet.x}% ${inclinaison.reflet.y}%, rgba(255,255,255,0.28), transparent 55%)`,
          }}
        />
      ) : null}
    </div>
  );
}

function Ligne({ nom, children }: { nom: string; children: React.ReactNode }): ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-b-0">
      <dt className="text-muted-foreground">{nom}</dt>
      <dd className="m-0 text-right">{children}</dd>
    </div>
  );
}

function Carte({ carte }: { carte: CartePublique }): ReactElement {
  const [signalement, setSignalement] = useState(false);
  const [copie, setCopie] = useState<"rien" | "ok" | "echec">("rien");
  const declencheur = useRef<HTMLButtonElement>(null);
  const adresse = adresseDePartage(window.location.origin, carte.ref);

  useEffect(() => {
    document.title = titreDeLOnglet(carte.nom, carte.rarete);
  }, [carte.nom, carte.rarete]);

  async function partager(): Promise<void> {
    try {
      await navigator.clipboard.writeText(adresse);
      setCopie("ok");
    } catch {
      setCopie("echec");
    }
  }

  function fermerLeSignalement(): void {
    setSignalement(false);
    // Le focus retourne au bouton qui a ouvert la fenetre.
    queueMicrotask(() => declencheur.current?.focus());
  }

  return (
    <article className="grid gap-8 md:grid-cols-[minmax(0,460px)_minmax(0,1fr)]">
      <section aria-label="La carte" className="flex flex-col gap-3">
        <ImageInclinable carte={carte} />
        <p className="m-0 text-center text-[20px] text-muted-foreground">{PAGE.aide}</p>
      </section>

      <div className="flex flex-col gap-5">
        <header className="flex flex-col gap-3">
          <h1 className="police-titre m-0 break-words text-[34px] leading-[1.1] text-[#FCDD8E] md:text-[44px]">
            {carte.nom}
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            <Rarete carte={carte} />
            <span className="text-muted-foreground">
              {carte.edition} · saison {carte.saison} · n° {carte.numeroAlbum}
            </span>
          </div>
        </header>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => void partager()}
            className={cn(
              BOUTON,
              "border-primary bg-primary text-primary-foreground hover:opacity-90",
            )}
          >
            {PAGE.partager}
          </button>
          <button
            ref={declencheur}
            type="button"
            onClick={() => setSignalement(true)}
            className={cn(BOUTON, "border-input bg-background hover:bg-muted")}
          >
            {PAGE.signaler}
          </button>
        </div>
        <div role="status" aria-live="polite" className="min-h-6 text-[20px]">
          {copie === "ok" ? <p className="m-0">{PAGE.copie}</p> : null}
          {copie === "echec" ? (
            <p className="m-0">
              {PAGE.copieImpossible} <code className="break-all">{adresse}</code>
            </p>
          ) : null}
        </div>

        <section
          aria-labelledby="chronique"
          className="rounded-md border-2 border-[var(--lisere)] bg-card p-4"
        >
          <h2 id="chronique" className="m-0 mb-2 text-[20px] font-normal text-muted-foreground">
            {carte.mentionIa ? PAGE.chroniqueIa : PAGE.chronique}
          </h2>
          <p className="m-0 text-[24px] leading-snug">
            {carte.chronique ?? (
              <span className="text-muted-foreground">{PAGE.chroniqueEnCours}</span>
            )}
          </p>
        </section>

        <section aria-labelledby="statistiques">
          <h2 id="statistiques" className="sr-only">
            {PAGE.statistiques}
          </h2>
          <ul className="m-0 grid list-none grid-cols-3 gap-3 p-0">
            {carte.stats.map((stat) => (
              <li
                key={stat.libelle}
                className="flex flex-col items-center rounded-md border-2 border-border bg-card px-2 py-3 text-center"
              >
                <span className="police-nom text-[34px] leading-none text-primary">
                  {stat.valeur}
                </span>
                <span className="text-[18px] text-muted-foreground">{stat.libelle}</span>
              </li>
            ))}
          </ul>
        </section>

        <section
          aria-labelledby="infos"
          className="rounded-md border-2 border-[var(--lisere)] bg-card p-4"
        >
          <h2 id="infos" className="police-titre m-0 mb-1 text-[26px] text-primary">
            {PAGE.titreInfos}
          </h2>
          <dl className="m-0">
            <Ligne nom={PAGE.rarete}>{nomRarete(carte.rarete)}</Ligne>
            <Ligne nom={PAGE.edition}>
              {editionEnToutesLettres(carte.edition)} · {etatEdition(carte.enCirculation)}
            </Ligne>
            {carte.tirageMax === null ? null : (
              <Ligne nom={PAGE.tirage}>{tirage(carte.exemplaires, carte.tirageMax)}</Ligne>
            )}
            <Ligne nom={PAGE.habitants}>{habitants(carte.possesseurs)}</Ligne>
          </dl>
        </section>
      </div>

      {signalement ? (
        <SignalementDialog reference={carte.ref} onFermer={fermerLeSignalement} />
      ) : null}
    </article>
  );
}

/** La carte n'existe pas, a ete retiree ou n'est pas publique : la meme page, sans dire pourquoi. */
export function CarteInconnue(): ReactElement {
  useEffect(() => {
    document.title = "Carte introuvable · La Taverne";
  }, []);
  return (
    <section className="flex flex-col items-start gap-3 py-8">
      <h1 className="police-titre m-0 text-[30px] text-primary md:text-[40px]">{INCONNUE.titre}</h1>
      <p className="m-0 text-muted-foreground">{INCONNUE.texte}</p>
      <a href={ACCUEIL_PATH} className={cn("min-h-11 text-[22px]", LIEN)}>
        {INCONNUE.lien}
      </a>
    </section>
  );
}

export function CartePubliquePage({ reference }: { reference: string }): ReactElement {
  const requete = useCartePublique(reference);
  const statut = (requete.error as { status?: number } | null)?.status;

  let contenu: ReactElement;
  if (requete.isPending) {
    contenu = (
      <p role="status" className="m-0 text-muted-foreground">
        {PAGE.chargement}
      </p>
    );
  } else if (requete.isError) {
    contenu =
      statut === 404 || statut === 400 ? (
        <CarteInconnue />
      ) : (
        <div className="flex flex-col items-start gap-3">
          <p role="alert" className="m-0 text-destructive">
            {ERREUR.lecture}
          </p>
          <Button className="text-[18px]" onClick={() => void requete.refetch()}>
            {ERREUR.relire}
          </Button>
        </div>
      );
  } else {
    contenu = <Carte carte={requete.data} />;
  }

  return (
    <div className="police-texte mx-auto flex w-full max-w-[1100px] flex-col gap-5 text-[22px] md:text-[24px]">
      <a
        href={ACCUEIL_PATH}
        className={cn("inline-flex min-h-11 w-fit items-center text-[20px]", LIEN)}
      >
        ← {PAGE.retour}
      </a>
      {contenu}
    </div>
  );
}
